import { HttpClient, HttpParams } from '@angular/common/http';
import { Injectable, inject } from '@angular/core';
import { endpoints, environment } from '@environment';
import {
  Category,
  CategorySortOrderResponse,
  CategoryType,
  CategoryVisibilityRequest,
  SmartCategorySortingResponse,
  PagedRequest,
  PagedResponse,
  UpsertCategoryRequest,
} from '@core/models';
import { Observable } from 'rxjs';
import { suppressGlobalErrorFeedback } from '@core/interceptors';

@Injectable({ providedIn: 'root' })
export class CategoryService {
  private readonly http = inject(HttpClient);
  private readonly baseUrl = `${environment.apiUrl}/${endpoints.v1.categories}`;

  getAll(
    includeArchived = false,
    categoryType?: CategoryType,
    includeHidden = true
  ): Observable<Category[]> {
    const params = this.buildParams(includeArchived, categoryType, includeHidden);
    return this.http.get<Category[]>(this.baseUrl, { params, context: suppressGlobalErrorFeedback() });
  }

  getPaged(
    request: PagedRequest,
    includeArchived = false,
    categoryType?: CategoryType,
    includeHidden = true
  ): Observable<PagedResponse<Category>> {
    const params = this.buildParams(includeArchived, categoryType, includeHidden);
    return this.http.post<PagedResponse<Category>>(
      `${this.baseUrl}/paged`,
      request,
      { params, context: suppressGlobalErrorFeedback() }
    );
  }

  create(payload: UpsertCategoryRequest): Observable<Category> {
    return this.http.post<Category>(this.baseUrl, payload, { context: suppressGlobalErrorFeedback() });
  }

  getNextSortOrder(categoryType: CategoryType): Observable<CategorySortOrderResponse> {
    const params = new HttpParams().set('categoryType', categoryType);
    return this.http.get<CategorySortOrderResponse>(
      `${this.baseUrl}/next-sort-order`,
      { params, context: suppressGlobalErrorFeedback() }
    );
  }

  setSmartSorting(isEnabled: boolean): Observable<SmartCategorySortingResponse> {
    return this.http.put<SmartCategorySortingResponse>(
      `${this.baseUrl}/smart-sorting`, { isEnabled }, { context: suppressGlobalErrorFeedback() }
    );
  }

  update(id: string, payload: UpsertCategoryRequest): Observable<Category> {
    return this.http.put<Category>(`${this.baseUrl}/${id}`, payload, { context: suppressGlobalErrorFeedback() });
  }

  archive(id: string): Observable<Category> {
    return this.http.post<Category>(`${this.baseUrl}/${id}/archive`, null, { context: suppressGlobalErrorFeedback() });
  }

  setVisibility(id: string, isVisible: boolean): Observable<Category> {
    const payload: CategoryVisibilityRequest = { isVisible };
    return this.http.post<Category>(`${this.baseUrl}/${id}/visibility`, payload, { context: suppressGlobalErrorFeedback() });
  }

  delete(id: string): Observable<void> {
    return this.http.delete<void>(`${this.baseUrl}/${id}`, { context: suppressGlobalErrorFeedback() });
  }

  private buildParams(
    includeArchived: boolean,
    categoryType?: CategoryType,
    includeHidden = true
  ): HttpParams {
    let params = new HttpParams()
      .set('includeArchived', includeArchived)
      .set('includeHidden', includeHidden);
    if (categoryType) {
      params = params.set('categoryType', categoryType);
    }

    return params;
  }
}
